import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Col,
  Container,
  Row,
  ProgressBar,
  Button,
  Modal,
  Alert,
  Table,
  Form,
  InputGroup,
  Badge,
  Spinner,
} from "react-bootstrap";
import PieChart from "./charts/PieChart";
import DoughnutChart from "./charts/DoughnutChart";
import WhatsAppAPI from "../api/WhatsAppAPI";
import moments from "moment-timezone";
import LeadReports from "./reports/LeadReports";
import moment from "moment"; // make sure you have moment installed
import { Link, useOutletContext } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Home = ({ selectedWhatsAppSetting, userInfo }) => {
  const { socket: outletSocket } = useOutletContext() || {};
  const [companyCount, setCompanyCount] = useState(0);
  const [moduleCount, setModuleCount] = useState(0);
  const [planCount, setPlanCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [campaignCount, setCampaignCount] = useState(0);
  const [campaignStatusForCharts, setCampaignStatusForCharts] = useState(null);
  const [templateCategoryForCharts, setTemplateCategoryForCharts] =
    useState(null);
  // initialize with zeros to avoid showing undefined for a split-second
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [validityPercentage, setValidityPercentage] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const homeDataFetchId = useRef(0);

  const [storageData, setStorageData] = useState({ used: 0, total: 5, base: 5, addon: 0, percentage: 0 });

  const [subscriptionReport, setSubscriptionReport] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportSearchQuery, setReportSearchQuery] = useState("");
  const [reportTab, setReportTab] = useState("expiringSoon");

  useEffect(() => {
    const fetchStorage = async () => {
      try {
        const result = await WhatsAppAPI.getCompanyStorage();
        if (result && result.success && result.storage) {
          const baseGB = Number(result.storage.base_storage_gb) || 5;
          const addonGB = Number(result.storage.addon_gb) || 0;
          const totalGB = baseGB + addonGB;

          const dbSize = Number(result.storage.db_size_bytes) || 0;
          const fileSize = Number(result.storage.file_size_bytes) || 0;
          const totalBytes = dbSize + fileSize;

          const usedGB = totalBytes / (1024 * 1024 * 1024);
          const percentage = Math.max(0, Math.min(100, (usedGB / totalGB) * 100));

          setStorageData({
            used: usedGB.toFixed(2),
            total: totalGB,
            base: baseGB,
            addon: addonGB,
            percentage: percentage
          });
        }
      } catch (err) {
        console.error("Error fetching storage:", err);
      }
    };

    fetchStorage();
  }, [userInfo]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!userInfo?.subscription?.end_date) return;
      const now = moments();
      const endDate = moments(userInfo.subscription.end_date);

      const remainingDays = endDate.diff(now, "days");
      const totalDays = endDate.diff(
        moments(userInfo.subscription.start_date),
        "days",
      ); // Fixed total days
      const percentage = Math.max(
        0,
        Math.min(100, (remainingDays / totalDays) * 100),
      );

      setValidityPercentage(percentage);

      // Calculate time units for display
      const remainingDuration = endDate.diff(now);
      const days = Math.floor(remainingDuration / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (remainingDuration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor(
        (remainingDuration % (1000 * 60 * 60)) / (1000 * 60),
      );
      const seconds = Math.floor((remainingDuration % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [userInfo?.subscription]);

  useEffect(() => {
    const requestId = ++homeDataFetchId.current;

    async function init() {
      let companyCount = 0,
        moduleCount = 0,
        planCount = 0,
        invoiceCount = 0,
        leadCount = 0,
        campaignCount = 0,
        campaignStatus = null,
        templateCategoryCount = null;

      // Wait until JWT user is applied in App; otherwise this effect runs once
      // with userrole undefined and again after setUserInfo → duplicate /common/home calls.
      if (!userInfo?.userrole) {
        return;
      }

      try {
        if (userInfo.userrole === "SYS_ADMIN") {
          setLoadingReport(true);
          const [companies, modules, plans, invoices, report] = await Promise.all([
            WhatsAppAPI.fetchCompany(true),
            WhatsAppAPI.getModuleData("active"),
            WhatsAppAPI.getPlanData("active"),
            WhatsAppAPI.getInvoicesRecord("Pending"),
            WhatsAppAPI.getSubscriptionReport().catch((err) => {
              console.error("Error fetching subscription report:", err);
              return [];
            }),
          ]);

          if (requestId !== homeDataFetchId.current) return;

          companyCount = companies.length || 0;
          moduleCount = modules.records?.length || 0;
          planCount = plans.records?.length || 0;
          invoiceCount = invoices.length || 0;
          setSubscriptionReport(report || []);
          setLoadingReport(false);
        } else if (selectedWhatsAppSetting) {
          const home = await WhatsAppAPI.fetchHomeSummary(
            selectedWhatsAppSetting,
          );
          if (requestId !== homeDataFetchId.current) return;

          if (home.success && home.data) {
            leadCount = home.data.leadCount || 0;
            campaignStatus = home.data.campaignStatus || null;
            campaignCount = Number(home.data.campaignTotal) || 0;
            templateCategoryCount = home.data.templateCategoryCount || null;
          }
        } else {
          leadCount = 0;
          campaignCount = 0;
          campaignStatus = null;
          templateCategoryCount = null;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }

      if (requestId !== homeDataFetchId.current) return;

      setCompanyCount(companyCount);
      setModuleCount(moduleCount);
      setPlanCount(planCount);
      setInvoiceCount(invoiceCount);
      setLeadCount(leadCount);
      setCampaignCount(campaignCount);
      setCampaignStatusForCharts(campaignStatus);
      setTemplateCategoryForCharts(templateCategoryCount);
    }
    init();
  }, [selectedWhatsAppSetting, userInfo.userrole]);

  useEffect(() => {
    if (!outletSocket) return undefined;

    const onAuthenticated = (status) => {
      if (!status) {
        setShowQRModal(true);
      }
    };

    outletSocket.on("authenticated", onAuthenticated);
    return () => {
      outletSocket.off("authenticated", onAuthenticated);
    };
  }, [outletSocket]);

  if (!userInfo) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col lg={12}>
            <div className="text-center">
              <h3>Loading...</h3>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Modern color scheme
  const colors = {
    primary: "#1976d2",
    secondary: "#42a5f5",
    success: "#00ad5b",
    info: "#239dd1",
    warning: "#debf31",
    danger: "#d3761f",
    light: "#f8f9fa",
    dark: "#343a40",
  };

  // reduced radius to match your preference
  const baseRadius = "8px";

  // Card style with subtle hover effect
  const cardStyle = {
    background: "#fff",
    borderRadius: baseRadius,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    border: "none",
    transition: "all 0.3s ease",
  };

  const hoverStyle = {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
  };

  // Format numbers with commas only when number type
  const formatNumber = (num) => {
    if (typeof num !== "number") return num;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const isSysAdmin = userInfo?.userrole === "SYS_ADMIN";

  const getFilteredReport = () => {
    return subscriptionReport.filter((item) => {
      const searchStr = `${item.company_name} ${item.adminemail} ${item.plan_name} ${item.tenantcode}`.toLowerCase();
      if (reportSearchQuery && !searchStr.includes(reportSearchQuery.toLowerCase())) {
        return false;
      }
      if (reportTab === "expiringSoon") {
        return item.days_left >= 0 && item.days_left <= 30 && item.company_active;
      }
      if (reportTab === "expired") {
        return item.days_left < 0 || !item.company_active;
      }
      if (reportTab === "external") {
        return item.is_external === true;
      }
      return true;
    });
  };

  const filteredReport = getFilteredReport();
  const expiringCount = subscriptionReport.filter(
    (item) => item.days_left >= 0 && item.days_left <= 30 && item.company_active
  ).length;
  const expiredCount = subscriptionReport.filter(
    (item) => item.days_left < 0 || !item.company_active
  ).length;
  const externalCount = subscriptionReport.filter(
    (item) => item.is_external === true
  ).length;
  const totalReportCount = subscriptionReport.length;

  const exportToCSV = () => {
    const headers = ["Name", "Techno Sales Member", "Start Date", "End Date", "Salesforce", "Status", "TL/  PM", "Price"];
    const rows = filteredReport.map((item) => {
      let statusText = "Active";
      if (!item.company_active) statusText = "Inactive Company";
      else if (item.days_left < 0) statusText = "Expired";
      else if (item.days_left >= 0 && item.days_left <= 30) statusText = "Expiring Soon";
      
      const formattedStartDate = moment(item.start_date).format("YYYY-MM-DD");
      const formattedEndDate = moment(item.end_date).format("YYYY-MM-DD");
      
      return [
        item.company_name,
        "", // Techno Sales Member
        formattedStartDate,
        formattedEndDate,
        "", // Salesforce
        statusText,
        "", // TL/ PM
        item.plan_price || "0"
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const filename = `subscriptions_${reportTab}_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isSysAdmin) {
    return (
      <Container fluid className="p-4 dashboard-container">
        {/* Welcome Header */}
        <Row className="mb-4">
          <Col lg={12}>
            <Card
              style={{
                border: "1px solid var(--bs-border-color, #d5e3dc)",
                borderRadius: baseRadius,
                boxShadow: "0 4px 16px rgba(15,23,42,0.07)",
                overflow: "hidden",
                position: "relative",
                background: "linear-gradient(145deg, var(--theme-color-3) 0%, #ffffff 55%)",
                color: "var(--text-color-2)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  background: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0aDE4djE4SDM2VjM0em0tMjQgMGgxOHYxOEgxMlYzNHptMC0yNGgxOHYxOEgxMlYxMHptMjQgMGgxOHYxOEgzNlYxMHoiLz48L2c+PC9nPjwvc3ZnPg==')",
                  opacity: 0.08,
                }}
              ></div>
              <Card.Body 
                className="p-4 d-flex flex-column flex-md-row justify-content-between align-items-center"
                style={{ position: "relative", zIndex: 1 }}
              >
                <div>
                  <h2 className="fw-bold mb-2">
                    🛡️ Admin Control Panel
                  </h2>
                  <p className="mb-0 opacity-75">
                    System-wide analytics & subscription reports for {moment().format("dddd, DD MMMM YYYY")}
                  </p>
                </div>
                <div className="mt-3 mt-md-0">
                  <a
                    href="https://metastatus.com/whatsapp-business-api"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary bg-white shadow-sm"
                    style={{ borderRadius: baseRadius }}
                  >
                    <i className="fa-solid fa-server me-2"></i> WhatsApp API Status
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Stats Cards */}
        <Row className="g-4 mb-4">
          {[
            {
              title: "Total Companies",
              count: companyCount,
              color: colors.success,
              icon: "fa-building",
              link: "/company",
            },
            {
              title: "Active Modules",
              count: moduleCount,
              color: colors.info,
              icon: "fa-cubes",
              link: "/module",
            },
            {
              title: "Active Plans",
              count: planCount,
              color: colors.primary,
              icon: "fa-tags",
              link: "/plan",
            },
            {
              title: "Pending Invoices",
              count: invoiceCount,
              color: colors.warning,
              icon: "fa-file-invoice-dollar",
              link: "/invoice",
            },
          ].map((item, idx) => (
            <Col xl={3} lg={6} md={6} sm={12} key={idx} className="d-flex">
              <Link to={item.link} style={{ textDecoration: "none", flex: 1 }}>
                <Card
                  className="flex-fill stats-card h-100"
                  style={cardStyle}
                  onMouseEnter={(e) => Object.assign(e.currentTarget.style, hoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.currentTarget.style, cardStyle)}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <div
                          className="p-3 rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            backgroundColor: `${item.color}15`,
                            width: "50px",
                            height: "50px",
                          }}
                        >
                          <i className={`fa-solid ${item.icon} fs-4`} style={{ color: item.color }}></i>
                        </div>
                        <div>
                          <h6 className="text-muted mb-0">{item.title}</h6>
                          <h3 className="mb-0 fw-bold">{formatNumber(item.count)}</h3>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>

        {/* Subscription Report Panel */}
        <Row className="mb-4">
          <Col lg={12}>
            <Card style={cardStyle} className="shadow-sm">
              <Card.Body className="p-4">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                  <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center">
                      <i className="fa-solid fa-file-shield text-primary me-2"></i>
                      Subscription Expiry & Status Report
                    </h4>
                    <p className="text-muted mb-0">Monitor active, expiring, and inactive company subscriptions across the platform</p>
                  </div>
                  <div className="mt-3 mt-md-0 d-flex flex-wrap gap-2 align-items-center">
                    <InputGroup className="shadow-sm rounded" style={{ borderRadius: baseRadius, minWidth: "280px" }}>
                      <InputGroup.Text className="bg-white border-end-0">
                        <i className="fa-solid fa-magnifying-glass text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search company, plan or email..."
                        value={reportSearchQuery}
                        onChange={(e) => setReportSearchQuery(e.target.value)}
                        className="border-start-0 ps-0"
                      />
                      {reportSearchQuery && (
                        <Button variant="link" className="bg-white border border-start-0 text-muted" onClick={() => setReportSearchQuery("")}>
                          <i className="fa-solid fa-xmark"></i>
                        </Button>
                      )}
                    </InputGroup>
                    <Button variant="outline-success" onClick={exportToCSV} className="shadow-sm px-3 py-2 fw-semibold d-flex align-items-center" style={{ borderRadius: baseRadius }}>
                      <i className="fa-solid fa-file-csv me-2 fs-5"></i> Export CSV
                    </Button>
                  </div>
                </div>

                {/* Styled Custom Tabs */}
                <div className="d-flex flex-wrap gap-2 mb-4 border-bottom pb-3">
                  {[
                    { id: "expiringSoon", label: "Expiring Soon (<= 30 Days)", count: expiringCount, color: "warning" },
                    { id: "expired", label: "Already Expired / Inactive", count: expiredCount, color: "danger" },
                    { id: "external", label: "External Companies", count: externalCount, color: "info" },
                    { id: "all", label: "All Subscriptions", count: totalReportCount, color: "secondary" }
                  ].map(tab => (
                    <Button
                      key={tab.id}
                      onClick={() => setReportTab(tab.id)}
                      variant={reportTab === tab.id ? tab.color : "light"}
                      className="px-4 py-2 d-flex align-items-center rounded-pill fw-semibold transition-all shadow-sm border"
                      style={{
                        transform: reportTab === tab.id ? "scale(1.03)" : "none",
                      }}
                    >
                      {tab.label}
                      <Badge bg={reportTab === tab.id ? "white" : tab.color} text={reportTab === tab.id ? tab.color : "white"} className="ms-2 px-2.5 py-1.5 rounded-circle shadow-sm">
                        {tab.count}
                      </Badge>
                    </Button>
                  ))}
                </div>

                {loadingReport ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p className="text-muted mb-0 fw-semibold">Loading subscription records...</p>
                  </div>
                ) : filteredReport.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-3 shadow-inner">
                    <i className="fa-solid fa-folder-open fs-1 text-muted mb-3 opacity-50"></i>
                    <h5 className="fw-bold text-secondary">No subscriptions found</h5>
                    <p className="text-muted mb-0">Try adjusting your search criteria or checking another tab.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover responsive className="align-middle mb-0 custom-table">
                      <thead className="table-light">
                        <tr className="border-bottom-2">
                          <th className="py-3">Company Details</th>
                          <th className="py-3">Plan Info</th>
                          <th className="py-3">Tenant Code</th>
                          <th className="py-3">Start Date</th>
                          <th className="py-3">End Date</th>
                          <th className="py-3">Days Left & Status</th>
                          <th className="py-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredReport.map((item) => {
                          const isExpired = item.days_left < 0 || !item.company_active;
                          const isExpiringSoon = item.days_left >= 0 && item.days_left <= 30 && item.company_active;

                          let statusBadge = null;
                          if (!item.company_active) {
                            statusBadge = <Badge bg="secondary" className="px-3 py-2 rounded-pill shadow-sm text-uppercase">Inactive Co.</Badge>;
                          } else if (isExpired) {
                            statusBadge = <Badge bg="danger" className="px-3 py-2 rounded-pill shadow-sm text-uppercase">Expired</Badge>;
                          } else if (isExpiringSoon) {
                            statusBadge = <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill shadow-sm text-uppercase">Expiring Soon</Badge>;
                          } else {
                            statusBadge = <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm text-uppercase">Active</Badge>;
                          }

                          return (
                            <tr key={item.subscription_id} className="transition-all hover-row">
                              <td className="py-3.5">
                                <div className="d-flex flex-column">
                                  <strong className="fs-6 text-dark">{item.company_name}</strong>
                                  <small className="text-muted d-flex align-items-center mt-1">
                                    <i className="fa-solid fa-envelope me-1.5 fs-7 opacity-75"></i>
                                    {item.adminemail || "No Email"}
                                  </small>
                                </div>
                              </td>
                              <td className="py-3.5">
                                <div className="d-flex flex-column">
                                  <strong className="text-primary">{item.plan_name}</strong>
                                  <small className="text-muted mt-1">{item.validity} Months Validity</small>
                                </div>
                              </td>
                              <td className="py-3.5 text-secondary fw-semibold">
                                <code>{item.tenantcode}</code>
                              </td>
                              <td className="py-3.5 text-muted">
                                {moment(item.start_date).format("DD MMM YYYY")}
                              </td>
                              <td className="py-3.5 text-dark fw-medium">
                                {moment(item.end_date).format("DD MMM YYYY")}
                              </td>
                              <td className="py-3.5">
                                <div className="d-flex flex-column align-items-start gap-1.5">
                                  {statusBadge}
                                  <span className="fs-7 font-monospace fw-semibold mt-1">
                                    {item.days_left < 0 ? (
                                      <span className="text-danger">
                                        <i className="fa-solid fa-triangle-exclamation me-1"></i>
                                        Expired {Math.abs(item.days_left)} days ago
                                      </span>
                                    ) : item.days_left === 0 ? (
                                      <span className="text-warning fw-bold">
                                        <i className="fa-solid fa-hourglass-half me-1"></i>
                                        Expires today!
                                      </span>
                                    ) : (
                                      <span className={isExpiringSoon ? "text-warning fw-semibold" : "text-success fw-semibold"}>
                                        <i className="fa-solid fa-clock me-1"></i>
                                        {item.days_left} days remaining
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3.5 text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="rounded-pill px-3 shadow-sm"
                                  href={`mailto:${item.adminemail}?subject=Watconnect Subscription Renewal Notice&body=Dear Admin,%0D%0DWe hope you are enjoying your subscription to Watconnect. This is a reminder that your plan (${item.plan_name}) ${item.days_left < 0 ? "has expired on " : "will expire on "}${moment(item.end_date).format("DD-MM-YYYY")}.%0D%0DPlease renew your plan to ensure uninterrupted access.%0D%0DBest regards,%0D%0AWatconnect Team`}
                                >
                                  <i className="fa-solid fa-paper-plane me-1.5"></i>
                                  Notify
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Custom CSS overrides for super admin dashboard */}
        <style>{`
          .custom-table th {
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #64748b;
          }
          .hover-row {
            transition: all 0.2s ease-in-out;
          }
          .hover-row:hover {
            background-color: rgba(25, 118, 210, 0.02) !important;
            transform: scale(1.002);
          }
          .transition-all {
            transition: all 0.3s ease;
          }
          .badge {
            font-weight: 600;
            letter-spacing: 0.3px;
          }
        `}</style>
        <ToastContainer autoClose={35000} />
      </Container>
    );
  }

  return (
    <Container fluid className="p-4 dashboard-container">
      {storageData.percentage >= 80 && storageData.percentage < 100 && (
        <Alert variant="warning" className="mb-4 d-flex align-items-center" style={{ borderRadius: baseRadius, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
          <div>
            <strong>Storage Warning:</strong> Your storage is at {storageData.percentage.toFixed(1)}%. Once it reaches 100%, you won't be able to store new attachments. Please clear some data or upgrade your plan.
          </div>
        </Alert>
      )}

      {storageData.percentage >= 100 && (
        <Alert variant="danger" className="mb-4 d-flex align-items-center" style={{ borderRadius: baseRadius, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <i className="bi bi-exclamation-octagon-fill fs-4 me-3"></i>
          <div>
            <strong>Storage Full:</strong> Your storage has reached its limit. You cannot store new attachments. Please clear some data or upgrade your plan.
          </div>
        </Alert>
      )}

      {/* Welcome Header */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card
            style={{
              border: "1px solid var(--bs-border-color, #d5e3dc)",
              borderRadius: baseRadius,
              color: "black",
              boxShadow: "0 4px 16px rgba(15,23,42,0.07)",
              overflow: "hidden",
              position: "relative",
              background:
                "linear-gradient(145deg, var(--theme-color-3) 0%, #ffffff 55%)",
              color: "var(--text-color-2)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                background:
                  "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0aDE4djE4SDM2VjM0em0tMjQgMGgxOHYxOEgxMlYzNHptMC0yNGgxOHYxOEgxMlYxMHptMjQgMGgxOHYxOEgzNlYxMHoiLz48L2c+PC9nPjwvc3ZnPg==')",
                opacity: 0.08,
              }}
            ></div>
            <Card.Body 
              className="p-4 d-flex flex-column flex-md-row justify-content-between align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div>
                <h2 className="fw-bold mb-2">
                  👋 Welcome,{" "}
                  {userInfo?.username
                    ? `${userInfo.username || ""}`
                    : userInfo?.email}
                </h2>
                <p className="mb-0 opacity-75">
                  Today is {moment().format("dddd, DD MMMM YYYY")}
                </p>
              </div>
              <div className="mt-3 mt-md-0">
                <a
                  href="https://metastatus.com/whatsapp-business-api"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary bg-white shadow-sm"
                  style={{ borderRadius: baseRadius }}
                >
                  <i className="fa-solid fa-server me-2"></i> WhatsApp API Status
                </a>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Plan Validity & Storage */}
      {userInfo.subscription && (
        <Row className="mb-4 g-4 align-items-stretch">
          <Col lg={6} md={12}>
            <Card
              style={{
                ...cardStyle,
                background: `linear-gradient(120deg, var(--theme-color-3) 0%, #ffffff 50%, rgba(0,173,91,0.06) 100%)`,
                border: "1px solid var(--bs-border-color, #d5e3dc)",
                borderLeft: `4px solid ${colors.success}`,
                color: "var(--text-color-2)",
                overflow: "hidden",
                height: "100%",
              }}
              className="position-relative h-100"
            >
              <div
                style={{
                  position: "absolute",
                  top: "-20px",
                  right: "-20px",
                  width: "120px",
                  height: "120px",
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: "50%",
                }}
              ></div>
              <Card.Body className="p-4 position-relative d-flex flex-column justify-content-center">
                <Row className="align-items-center">
                  <Col
                    lg={12}
                    className="text-start mb-3"
                  >
                    <div className="mb-2 d-flex align-items-center">
                      <i className="fa-solid fa-crown me-2 fs-5"></i>
                      <strong className="fs-6">
                        {userInfo.plan?.name || "Basic"} Plan
                      </strong>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="fa-solid fa-clock me-2 fs-6"></i>
                      <span className="fs-6">
                        Valid until{" "}
                        {userInfo.subscription.end_date
                          ? moments(userInfo.subscription.end_date).format(
                            "MMMM D, YYYY",
                          )
                          : "N/A"}
                      </span>
                    </div>
                  </Col>

                  <Col lg={12} className="text-start mt-2">
                    <div className="mb-2 d-flex align-items-center justify-content-between">
                      <small className="opacity-75">
                        Validity {Math.round(validityPercentage)}%
                      </small>
                      <i className="fa-solid fa-info-circle opacity-50"></i>
                    </div>
                    <ProgressBar
                      now={validityPercentage}
                      style={{
                        height: "15px",
                        borderRadius: "6px",
                      }}
                      className="custom-progress"
                      label={`${Math.round(validityPercentage)}%`}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Storage Usage Widget */}
          <Col lg={6} md={12}>
            <Card
              style={{
                ...cardStyle,
                background: `linear-gradient(120deg, var(--theme-color-3) 0%, #ffffff 50%, rgba(25, 118, 210, 0.06) 100%)`,
                border: "1px solid var(--bs-border-color, #d5e3dc)",
                borderLeft: `4px solid ${colors.primary}`,
                color: "var(--text-color-2)",
                overflow: "hidden",
                height: "100%",
              }}
              className="position-relative h-100"
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "-30px",
                  left: "-30px",
                  width: "100px",
                  height: "100px",
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: "50%",
                }}
              ></div>
              <Card.Body className="p-4 position-relative d-flex flex-column justify-content-center">
                <Row className="align-items-center">
                  <Col lg={12} className="text-start mb-3">
                    <div className="mb-2 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="fa-solid fa-database me-2 fs-5" style={{ color: colors.primary }}></i>
                        <strong className="fs-6">Storage Usage</strong>
                      </div>
                      {storageData.percentage >= 90 && (
                        <span className="badge bg-danger rounded-pill px-3 py-2 shadow-sm animate__animated animate__pulse animate__infinite">
                          <i className="fa-solid fa-triangle-exclamation me-1"></i> Running out of space
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-center text-muted mt-2">
                      <i className="fa-regular fa-hard-drive me-2"></i>
                      <span>
                        Includes Database & Attachment Files
                      </span>
                    </div>
                  </Col>

                  <Col lg={12} className="text-start mt-2">
                    <div className="mb-2 d-flex justify-content-between align-items-center">
                      <span className="fw-bold fs-5">
                        {storageData.used} GB{" "}
                        <span className="fw-normal text-muted fs-6">
                          / {storageData.total} GB
                          {storageData.addon > 0 && (
                            <span className="ms-2 text-muted fw-normal" style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                              ({storageData.base} GB Plan + {storageData.addon} GB Addons)
                            </span>
                          )}
                        </span>
                      </span>
                      <small className="fw-semibold" style={{ color: storageData.percentage > 85 ? colors.danger : colors.primary }}>
                        {Math.round(storageData.percentage)}% Used
                      </small>
                    </div>
                    <ProgressBar
                      now={storageData.percentage}
                      variant={storageData.percentage > 85 ? "danger" : storageData.percentage > 70 ? "warning" : "info"}
                      style={{
                        height: "15px",
                        borderRadius: "6px",
                      }}
                      className="shadow-sm"
                    />

                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Stats Cards */}
      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        {[
          {
            title: "Leads",
            count: leadCount,
            color: colors.success,
            icon: "fa-bolt",
            link: "/leads",
          },
          {
            title: "Campaigns",
            count: campaignCount,
            color: colors.info,
            icon: "fa-user-group",
            link: "/campaign",
          },
          {
            title: "Marketing templates",
            count: Number(templateCategoryForCharts?.MARKETING) || 0,
            color: colors.primary,
            icon: "fa-bullhorn",
            link: "/whatsapp_template",
          },
          {
            title: "Utility templates",
            count: Number(templateCategoryForCharts?.UTILITY) || 0,
            color: colors.warning,
            icon: "fa-wrench",
            link: "/whatsapp_template",
          },
        ].map((item, idx) => (
          <Col xl={3} lg={6} md={6} sm={12} key={idx} className="d-flex">
            <Link to={item.link} style={{ textDecoration: "none", flex: 1 }}>
              <Card
                className="flex-fill stats-card h-100"
                style={cardStyle}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, hoverStyle)
                }
                onMouseLeave={(e) =>
                  Object.assign(e.currentTarget.style, cardStyle)
                }
              >
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div
                        className="p-3 rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          backgroundColor: `${item.color}15`,
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                        }}
                      >
                        <i
                          className={`fa-solid ${item.icon} fs-4`}
                          style={{ color: item.color }}
                        ></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-0">{item.title}</h6>
                        <h3 className="mb-0 fw-bold">
                          {formatNumber(item.count)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {/* Charts & QR Code Section (make both columns stretch to equal height) */}
      <Row className="g-4 mb-4 align-items-stretch">
        {/* Lead Reports wrapped in a Card so it matches QR card height */}
        <Col xl={8} lg={7} md={12}>
          <Card style={cardStyle} className="h-100">
            <Card.Body className="h-100 d-flex flex-column">
              <LeadReports selectedWhatsAppSetting={selectedWhatsAppSetting} />
            </Card.Body>
          </Card>
        </Col>

        {/* QR Code */}
        <Col xl={4} lg={5} md={12}>
          <Card style={cardStyle} className="h-100">
            <Card.Body className="d-flex flex-column h-100">
              <div className="text-center mb-3">
                <h5 className="fw-bold mb-1">📱 Download Our App</h5>
                <small className="text-muted">
                  Scan the QR code to get started
                </small>
              </div>
              <div
                className="d-flex flex-column align-items-center justify-content-center p-4 mb-3 flex-grow-1"
                style={{
                  background: "linear-gradient(135deg, #f8fafc, #e2e8f0)",
                  borderRadius: baseRadius,
                }}
              >
                <div
                  className="p-3 bg-white rounded shadow-sm mb-3"
                  style={{ borderRadius: "6px" }}
                >
                  <img
                    src="watconnect-qr.png"
                    alt="QR code"
                    style={{ width: 150, height: 150 }}
                  />
                </div>
                <div className="text-center">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.wat.connect"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block mb-2"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt="Get it on Google Play"
                      style={{ height: 45 }}
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/watconnect/id6745876864"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block"
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      style={{ height: 45 }}
                    />
                  </a>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="g-4 mb-4">
        <Col xl={6} lg={6} md={12}>
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 d-flex align-items-center">
                  Campaign Analytics
                </h5>
              </div>
              <div style={{ height: "300px" }}>
                <PieChart campaignStatus={campaignStatusForCharts} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6} lg={6} md={12}>
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 d-flex align-items-center">
                  WhatsApp Templates
                </h5>
              </div>
              <div style={{ height: "300px" }}>
                <DoughnutChart
                  templateCategoryCount={templateCategoryForCharts}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* WhatsApp Connection Status Modal */}
      <Modal show={showQRModal} onHide={() => setShowQRModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>WhatsApp Connection</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <i className="fas fa-qrcode fs-1 text-primary"></i>
          </div>
          <h5>WhatsApp Authentication Required</h5>
          <p className="text-muted">
            Please scan the QR code to connect your WhatsApp account.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQRModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => setShowQRModal(false)}>
            Scan Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS adjustments (reduced radius + small tweaks) */}
      <style>{`
        .btn-tab {
          border: none;
          border-radius: 0;
          padding: 1rem 1.5rem;
          color: #6c757d;
          background: transparent;
          position: relative;
          transition: all 0.3s;
        }
        
        .btn-tab:hover {
          color: ${colors.primary};
          background: rgba(25, 118, 210, 0.05);
        }
        
        .btn-tab.active {
          color: ${colors.primary};
          font-weight: 600;
        }
        
        .btn-tab.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: ${colors.primary};
          border-radius: 6px 6px 0 0;
        }
        
        .stats-card:hover .custom-progress .progress-bar {
          background-color: #1976d2;
        }
        
        .progress-bar.custom-progress {
          background-color: #dee2e6;
        }
        
        .progress-bar.custom-progress .progress-bar {
          background-color: #6c757d;
          transition: background-color 0.3s;
        }

        /* ensure bootstrap cards in this dashboard have smaller radius */
        .dashboard-container .card {
          border-radius: ${baseRadius} !important;
        }

      `}</style>
      <ToastContainer autoClose={35000} />
    </Container>
  );
};

export default Home;
